import type { Bid } from 'prebid.js'

import type { AdProviderTypes } from '@/types'
import type { AvailablePGEvents, LoggerEvent, StartRumEvent } from '@/types/logger.types'
import { timer } from '@/utils/helpers'
import { isElementViewable } from '@/utils/is-element-viewable'

import type { AdLoader } from './ad-loader'
import type { AdProvider } from './ad-provider'
import { AuctionTracker } from './auction-tracker'
import type { Aps } from './header-bidding/aps'
import type { HeaderBidding } from './header-bidding/prebid'
import type { HbAdUnitDefinition } from './header-bidding/prebid/types'
import { getSspAuctionId } from './header-bidding/prebid/utils/ssp-auction-id'
import { OutOfPageAd } from './out-of-page-ad'
import { PageAd } from './page-ad'
import type { ProviderOutOfPageAd } from './providers/shared/provider-out-of-page-ad'
import type { ProviderPageAd } from './providers/shared/provider-page-ad'
import {
    type AdFailureKind,
    type AdProviderRequest,
    type HbAdUnitRequest,
    AdProviderStatus,
    type AdTargeting,
    type AdType,
    AdPlacementType,
    type OutOfPageAdType,
    type PageAdOptions,
    type PageVideoAdOptions,
} from './types'
import { AdConsoleLogger } from './utils/ad-console-logger'
import { createRumTracker } from './utils/create-rum-tracker'
import { generateAdElementId } from './utils/generate-ad-id'
import { mergeTargeting } from './utils/merge-targeting'

export type ProviderKey = AdType | '*'

export type FailedAdProvider = {
    readonly failed: true
    readonly type: AdProviderTypes
    readonly errorMessage: string
}

export type LoadingProvider = {
    readonly loading: true
    readonly type: AdProviderTypes
    readonly timeout: number
}

type ProviderConfig = {
    type: AdProviderTypes
    timeout: number
}

export type PlacementGroup<T> = {
    placements: ProviderKey[]
    settings: T[]
}

export type AdServiceOptions = {
    targeting: AdTargeting
    logEvent: (event: LoggerEvent) => void
    registerPageHideFallbackEvent: (event: Omit<LoggerEvent, 'dimension1'>) => () => void
    startRumEvent: StartRumEvent
    waitForHeaderBidding?: boolean
    consentReady?: Promise<void>
    auid: string
    providersSettings: PlacementGroup<ProviderConfig>[]
    useFallbackAuctionReuse?: boolean
}

const SERVICE_INITIALIZATION_TIMEOUT = 10000

const FAILURE_EVENT_NAMES: Record<AdFailureKind, AvailablePGEvents> = {
    empty: 'ad_empty',
    error: 'ad_error',
    timeout: 'ad_timeout',
}

const FAILURE_RUM_STATUSES: Record<AdFailureKind, 'empty' | 'error'> = {
    empty: 'empty',
    error: 'error',
    timeout: 'error',
}

function isActiveProvider(provider: AdProvider | FailedAdProvider | LoadingProvider): provider is AdProvider {
    return !('loading' in provider) && !('failed' in provider)
}

export class AdService {
    private readonly options: AdServiceOptions

    private readonly logEvent: (event: LoggerEvent) => void

    private readonly startRumEvent: StartRumEvent

    private providers: Partial<Record<ProviderKey, (AdProvider | FailedAdProvider | LoadingProvider)[]>> = {}

    private currentTargeting: AdTargeting = {}

    private headerBidding: HeaderBidding | undefined = undefined

    private aps: Aps | undefined = undefined

    private readonly headerBiddingReadyPromise: Promise<{ success: boolean; error?: string }>
    private headerBiddingReadyResolve!: (result: { success: boolean; error?: string }) => void

    private depsReady = false
    private serviceReady = false

    serviceReadyPromise: Promise<AdProviderStatus>
    private serviceReadyResolve: (status: AdProviderStatus) => void

    /** Times the one-time wait every early ad request sits through: construction → service ready. */
    private serviceReadyTracker: ReturnType<typeof createRumTracker>

    pageBanners: Set<PageAd> = new Set()

    private readonly refreshTimers: Map<PageAd, number> = new Map()

    outOfPageBanners: Set<OutOfPageAd> = new Set()

    adConsoleLogger = new AdConsoleLogger('service')

    constructor(options: AdServiceOptions) {
        this.adConsoleLogger.debug('create service', { options })
        this.options = options
        this.logEvent = options.logEvent
        this.startRumEvent = options.startRumEvent
        options.providersSettings.forEach(({ placements, settings }) => {
            placements.forEach((placement) => {
                this.providers[placement] = settings.map(({ type, timeout }) => ({ loading: true, type, timeout }))
            })
        })
        this.currentTargeting = options.targeting

        const { promise, resolve } = Promise.withResolvers<AdProviderStatus>()
        this.serviceReadyPromise = promise
        this.serviceReadyResolve = resolve

        const hbReady = Promise.withResolvers<{ success: boolean; error?: string }>()
        this.headerBiddingReadyPromise = hbReady.promise
        this.headerBiddingReadyResolve = hbReady.resolve
        if (!options.waitForHeaderBidding) {
            this.headerBiddingReadyResolve({ success: true })
        }

        this.serviceReadyTracker = createRumTracker('ad-service-ready', this.startRumEvent)

        timer(SERVICE_INITIALIZATION_TIMEOUT).then(() => this.markServiceReady('timeout'))
        this.initDeps()
    }

    setProvider(index: number, provider: AdProvider | FailedAdProvider, type: ProviderKey = '*') {
        const arr = this.providers[type]
        if (arr && index < arr.length) {
            arr[index] = provider
        }

        if ('failed' in provider) {
            this.adConsoleLogger.debug(`provider failed`, { type, index, error: provider.errorMessage })
        } else {
            this.adConsoleLogger.debug(`provider ready`, { type, index, provider: provider.type })
            this.setProviderTargeting(provider, this.currentTargeting)
        }
        this.checkServiceReady()
    }

    private async initDeps() {
        const tracker = createRumTracker('ad-service-deps-init', this.startRumEvent)
        await (this.options.consentReady ?? Promise.resolve())

        const headerBiddingResult = await this.headerBiddingReadyPromise
        tracker.send({
            status: headerBiddingResult.success ? 'success' : 'error',
            dimension1: headerBiddingResult.error,
        })
        this.depsReady = true
        this.checkServiceReady()
    }

    setHeaderBidding(headerBidding: HeaderBidding | undefined): void {
        this.headerBidding = headerBidding
        headerBidding?.updateTargeting(this.currentTargeting)
        headerBidding?.initialized.then((result) => this.headerBiddingReadyResolve(result))
    }

    setAps(aps: Aps | undefined): void {
        this.aps = aps
    }

    private checkServiceReady() {
        if (this.serviceReady) {
            return
        }

        const allTypesReady = Object.values(this.providers).every((slots) => this.isTypeReady(slots))

        if (allTypesReady && this.depsReady) {
            this.markServiceReady()
        }
    }

    private markServiceReady(reason?: string) {
        if (!this.serviceReady) {
            const readiness = reason === 'timeout' ? this.getReadinessSnapshot() : undefined

            if (readiness) {
                this.adConsoleLogger.error('service ready via timeout fallback - still pending:', readiness)
            }

            this.serviceReadyTracker.send({
                status: reason === 'timeout' ? 'error' : 'success',
                dimension1: readiness,
            })
            this.logEvent({
                event: 'custom_event',
                eventName: 'ad_service_ready',
                dimension3: readiness,
            })

            this.serviceReady = true
            this.adConsoleLogger.debug(reason ? `service ready (${reason})` : 'service ready')
            this.serviceReadyResolve(AdProviderStatus.online)
        }
    }

    private isTypeReady(slots: (AdProvider | FailedAdProvider | LoadingProvider)[]): boolean {
        return (
            slots.some((p): p is AdProvider => !('loading' in p) && !('failed' in p) && !!p.blocksServiceReady) ||
            slots.every((p) => !('loading' in p))
        )
    }

    /**
     * Snapshot of why the natural (non-timeout) readiness check hasn't fired yet: whether deps are
     * still pending, and which provider types still have loading slots without a blocksServiceReady
     * provider. Only meaningful when logged right as the SERVICE_INITIALIZATION_TIMEOUT fallback fires.
     */
    private getReadinessSnapshot(): string {
        const pendingTypes = Object.entries(this.providers)
            .filter(([, slots]) => !!slots && !this.isTypeReady(slots))
            .map(([type, slots]) => `${type}:${slots?.filter((p) => 'loading' in p).length}/${slots?.length}loading`)

        return `depsReady=${this.depsReady};pendingTypes=${pendingTypes.join(',') || 'none'}`
    }

    private getProvidersByType(type: ProviderKey): Array<AdProvider | FailedAdProvider | LoadingProvider> {
        return this.providers[type] || this.providers['*'] || []
    }

    private async requestAd<T extends ProviderPageAd | ProviderOutOfPageAd>(
        providerMethod: (provider: AdProvider, requestOptions: AdProviderRequest) => T,
        publicBanner: {
            setIntermediateInternalBanner: (internalBanner: T) => void
            setInternalBanner: (internalBanner: T) => void
            triggerEmpty: (reason: string) => void
        },
        {
            placement,
            slotElementId,
            getHbAdUnit,
        }: {
            placement: AdType
            slotElementId?: string
            getHbAdUnit?: (provider: AdProvider, requestOptions: HbAdUnitRequest) => HbAdUnitDefinition | undefined
        },
    ): Promise<boolean> {
        const requestTracker = createRumTracker('request-ad', this.startRumEvent)
        const requestContext = { targeting: { ad_placement: placement } } as const
        const providerSlots = this.getProvidersByType(placement)
        this.adConsoleLogger.debug(`waterfall start`, { placement, providers: providerSlots.length })

        const auctionTracker = new AuctionTracker(providerSlots.length, {
            headerBidding: this.headerBidding,
            logEvent: this.logEvent,
            adConsoleLogger: this.adConsoleLogger,
        })

        for (let i = 0; i < providerSlots.length; i++) {
            const stepTracker = createRumTracker('request-ad-step', this.startRumEvent)

            const orderAdValue = i + 1
            const currentProvider = providerSlots[i]
            const cancelPageHiddenTracking = this.options.registerPageHideFallbackEvent({
                event: 'custom_event',
                eventName: 'ad_request_attempt_failed',
                adPlacement: placement,
                adProvider: currentProvider?.type,
                waterfallIndex: orderAdValue,
                dimension3: 'page_hide_fallback',
            })

            this.adConsoleLogger.debug(`waterfall attempt [${orderAdValue}/${providerSlots.length}]`, {
                placement,
                provider: currentProvider?.type || 'unknown',
            })

            const logWithoutBannerInfo = (
                eventName: AvailablePGEvents,
                rest?: Partial<Omit<LoggerEvent, 'dimension1' | 'dimension2'>>,
            ) => {
                this.logEvent({
                    event: 'custom_event',
                    eventName,
                    adPlacement: placement,
                    adProvider: currentProvider?.type,
                    waterfallIndex: orderAdValue,
                    ...rest,
                })
            }

            logWithoutBannerInfo('ad_waterfall_attempt', { dimension3: `${providerSlots.length}` })

            if (!currentProvider || 'failed' in currentProvider || 'loading' in currentProvider) {
                let logErrorMessage = 'Provider is not initialized: unexpected error'

                if (currentProvider && 'failed' in currentProvider) {
                    logErrorMessage = `Failed to load provider: ${currentProvider.errorMessage}`
                }

                if (currentProvider && 'loading' in currentProvider) {
                    logErrorMessage = 'Provider is not initialized'
                }

                stepTracker.send({
                    status: 'error',
                    dimension2: logErrorMessage,
                    dimension4: placement,
                    dimension3: `${orderAdValue}`,
                })
                logWithoutBannerInfo('ad_request_attempt_failed', {
                    errorMessage: logErrorMessage,
                    dimension3: logErrorMessage,
                })
                cancelPageHiddenTracking()

                continue
            }

            const hbAdUnit = getHbAdUnit?.(currentProvider, { headerBidding: this.headerBidding, requestContext })

            const internalBanner = providerMethod(currentProvider, {
                headerBidding: this.headerBidding,
                pendingAuction: auctionTracker.resolveAuction(
                    i,
                    hbAdUnit,
                    { placement, provider: currentProvider.type, requestContext },
                    !!this.options.useFallbackAuctionReuse && !!currentProvider.supportsFallbackAuctionReuse,
                ),
                pendingApsTargeting:
                    currentProvider.apstag && hbAdUnit
                        ? this.aps?.fetchBids(hbAdUnit, {
                              placement,
                              provider: currentProvider.type,
                              slotElementId,
                          })
                        : undefined,
                requestContext,
            })

            const log = (
                eventName: AvailablePGEvents,
                rest?: Partial<Omit<LoggerEvent, 'dimension1' | 'dimension2'>>,
                fallbackBid?: Bid,
            ) => {
                const auctionResult = internalBanner?.headerBiddingAuctionResult
                const hbBid = auctionResult?.renderedBid || fallbackBid

                this.logEvent({
                    event: 'custom_event',
                    eventName,
                    adPlacement: placement,
                    adUnit: internalBanner?.adUnit,
                    adHbCpmUsd: hbBid?.cpm,
                    adHbBidder: hbBid?.bidder,
                    adHbRequestId: hbBid?.requestId || undefined,
                    adProvider: currentProvider.type,
                    instreamDuration:
                        internalBanner?.type === AdPlacementType.OUT_OF_PAGE
                            ? internalBanner?.instreamDuration
                            : undefined,
                    waterfallIndex: orderAdValue,
                    dimension1: internalBanner?.headerBiddingAuctionResult?.winningBid?.mediaType,
                    dimension2: getSspAuctionId(auctionResult?.winningBid),
                    ...rest,
                })
            }

            publicBanner.setIntermediateInternalBanner(internalBanner)

            internalBanner.addEventListener('request', () => {
                log('ad_request')
            })

            internalBanner.addEventListener('unexpected_behavior', (e) => {
                log('ad_unexpected_behavior', { dimension3: e.detail?.reason })
            })

            internalBanner.addEventListener('native_no_fill', () => {
                log('ad_empty')
            })

            internalBanner.addEventListener('autoplay_check', (e) => {
                log('ad_autoplay_check', { dimension3: e.detail?.reason })
            })

            internalBanner.addEventListener('slot_stage', (e) => {
                log(
                    'ad_gam_slot_stage',
                    { dimension3: e.detail?.reason },
                    internalBanner.headerBiddingAuctionResult?.winningBid,
                )
            })

            let readyToViewableTracker: ReturnType<typeof createRumTracker> | undefined
            let readyToViewableSettled = false

            const commonSuccessTrackerData = {
                status: 'success',
                dimension3: `${orderAdValue}`,
                dimension4: placement,
            } as const

            internalBanner.addEventListener('viewable', (e) => {
                this.headerBidding?.markAuctionResultUsed(internalBanner.headerBiddingAuctionResult)
                if (i === 0) {
                    log('ad_show_high_value')
                }
                if (readyToViewableTracker && !readyToViewableSettled) {
                    readyToViewableSettled = true
                    readyToViewableTracker.send({
                        ...commonSuccessTrackerData,
                        dimension1: currentProvider.type,
                        dimension2: internalBanner.adUnit,
                    })
                }
                log('ad_show', { dimension3: e.detail?.gamResponse?.advertiserId?.toString() })
            })

            if (internalBanner.type === AdPlacementType.IN_PAGE) {
                internalBanner.addEventListener('showing', () => {
                    log('ad_rendered')
                })
            }

            const succeeded = await new Promise<boolean>((resolve) => {
                internalBanner.addEventListener(
                    internalBanner.type === AdPlacementType.OUT_OF_PAGE ? 'ready' : 'showing',
                    () => {
                        stepTracker.send({
                            ...commonSuccessTrackerData,
                            dimension1: currentProvider.type,
                            dimension2: internalBanner.adUnit,
                        })
                        requestTracker.send(commonSuccessTrackerData)
                        readyToViewableTracker = createRumTracker('ad-ready-to-viewable', this.startRumEvent)
                        resolve(true)
                    },
                    { once: true },
                )
                internalBanner.addEventListener(
                    'failed',
                    (e) => {
                        const kind = e.detail?.kind ?? 'error'
                        log(
                            FAILURE_EVENT_NAMES[kind],
                            { dimension3: e.detail?.reason },
                            internalBanner.headerBiddingAuctionResult?.winningBid,
                        )
                        const failureTrackerData = {
                            status: FAILURE_RUM_STATUSES[kind],
                            dimension4: placement,
                            dimension1: currentProvider.type,
                            dimension2: internalBanner.adUnit,
                            dimension3: `${orderAdValue}`,
                        } as const
                        if (!readyToViewableTracker) {
                            stepTracker.send(failureTrackerData)
                        } else if (!readyToViewableSettled) {
                            readyToViewableSettled = true
                            readyToViewableTracker.send(failureTrackerData)
                        }
                        resolve(false)
                    },
                    { once: true },
                )
            })

            cancelPageHiddenTracking()

            if (succeeded) {
                publicBanner.setInternalBanner(internalBanner)
                return true
            }
        }

        this.adConsoleLogger.debug(`waterfall exhausted, no fill`, { placement, attempts: providerSlots.length })

        requestTracker.send({
            status: 'empty',
            dimension4: placement,
        })

        publicBanner.triggerEmpty('unfilled')
        return false
    }

    requestOutOfPageAd(
        placement: OutOfPageAdType,
        { loader, adRootElementId }: { loader?: AdLoader; adRootElementId?: string } = {},
    ): OutOfPageAd {
        const publicBanner = new OutOfPageAd({
            placement,
            onDestroy: () => {
                this.outOfPageBanners.delete(publicBanner)
            },
            loader,
            timeout: this.getAdMaxTimeout(placement),
            logEvent: (eventName: AvailablePGEvents) => {
                this.logEvent({
                    event: 'custom_event',
                    eventName,
                    adPlacement: placement,
                    adUnit: publicBanner.adUnit,
                    adProvider: publicBanner.provider,
                })
            },
        })

        this.outOfPageBanners.add(publicBanner)
        this.adConsoleLogger.debug(`ad requested`, { placement })

        this.yieldToListeners().then(async () => {
            await this.serviceReadyPromise
            const succeeded = await this.requestAd<ProviderOutOfPageAd>(
                (provider, requestOptions) =>
                    provider.prepareOutOfPageAd(placement, { ...requestOptions, adRootElementId }),
                publicBanner,
                {
                    placement,
                    getHbAdUnit: (provider, requestOptions) =>
                        provider.getOutOfPageHbAdUnit?.(placement, { ...requestOptions, adRootElementId }),
                },
            )
            if (succeeded) {
                publicBanner.triggerReady()
            }
        })

        return publicBanner
    }

    requestPageAd(options: PageAdOptions): PageAd {
        const publicBanner: PageAd = new PageAd({
            placement: options.placement,
            onDestroy: () => {
                this.pageBanners.delete(publicBanner)
                window.clearTimeout(this.refreshTimers.get(publicBanner))
                this.refreshTimers.delete(publicBanner)
            },
            onRefresh: () => {
                this.loadPageAd(publicBanner, options)
            },
        })

        this.pageBanners.add(publicBanner)
        this.adConsoleLogger.debug(`ad requested`, { placement: options.placement })

        this.yieldToListeners().then(async () => {
            await this.loadPageAd(publicBanner, options)
            if (options.refresh) {
                this.scheduleRefresh(publicBanner, options.refresh)
            }
        })

        return publicBanner
    }

    requestVideoPageAd(options: PageVideoAdOptions): PageAd {
        const publicBanner: PageAd = new PageAd({
            placement: options.placement,
            onDestroy: () => {
                this.pageBanners.delete(publicBanner)
            },
        })

        this.pageBanners.add(publicBanner)
        this.adConsoleLogger.debug(`video ad requested`, { placement: options.placement })

        this.yieldToListeners().then(async () => {
            await this.loadInPageAd({
                publicBanner,
                options,
                request: (provider, requestOptions, containerElId) =>
                    provider.requestVideoPageAd({ ...options, el: containerElId }, requestOptions),
                getHbAdUnit: (provider, requestOptions, containerElId) =>
                    provider.getInPageVideoHbAdUnit?.({ ...options, el: containerElId }, requestOptions),
                fillContainer: true,
            })
        })

        return publicBanner
    }

    private async loadPageAd(publicBanner: PageAd, options: PageAdOptions): Promise<void> {
        await this.loadInPageAd({
            publicBanner,
            options,
            request: (provider, requestOptions, containerElId) =>
                provider.requestPageAd({ ...options, el: containerElId }, requestOptions),
            getHbAdUnit: (provider, requestOptions, containerElId) =>
                provider.getInPageHbAdUnit?.({ ...options, el: containerElId }, requestOptions),
        })
    }

    private async loadInPageAd({
        publicBanner,
        options,
        request,
        getHbAdUnit,
        fillContainer,
    }: {
        publicBanner: PageAd
        options: PageAdOptions | PageVideoAdOptions
        request: (provider: AdProvider, requestOptions: AdProviderRequest, containerElId: string) => ProviderPageAd
        getHbAdUnit: (
            provider: AdProvider,
            requestOptions: HbAdUnitRequest,
            containerElId: string,
        ) => HbAdUnitDefinition | undefined
        fillContainer?: boolean
    }): Promise<void> {
        await this.serviceReadyPromise

        // Banner may have been destroyed while awaiting serviceReadyPromise (e.g. ad-free mode activated)
        if (!this.pageBanners.has(publicBanner)) {
            return
        }

        const container = this.createAdContainer(options.el, fillContainer)

        if (!container) {
            // the target slot element disappeared from the DOM before the ad could be requested
            this.logEvent({
                event: 'custom_event',
                eventName: 'ad_error',
                adPlacement: options.placement,
                dimension3: 'no_container',
            })
            publicBanner.triggerEmpty('no_container')
            return
        }

        const succeeded = await this.requestAd<ProviderPageAd>(
            (provider, requestOptions) => request(provider, requestOptions, container.id),
            {
                setInternalBanner: (internalBanner) => publicBanner.setInternalBanner(internalBanner, container),
                setIntermediateInternalBanner: (internalBanner) =>
                    publicBanner.setIntermediateInternalBanner(internalBanner),
                triggerEmpty: (reason) => publicBanner.triggerEmpty(reason),
            },
            {
                placement: options.placement,
                slotElementId: container.id,
                getHbAdUnit: (provider, requestOptions) => getHbAdUnit(provider, requestOptions, container.id),
            },
        )

        if (succeeded) {
            publicBanner.triggerRendered()
        } else {
            container.remove()
        }
    }

    /** Defer past the microtask queue so callers can attach listeners before any provider event fires. */
    private yieldToListeners(): Promise<void> {
        return timer(10)
    }

    private createAdContainer(slotId: string, fillSlot = false): HTMLElement | null {
        const slot = document.getElementById(slotId)
        if (!slot) {
            return null
        }
        const container = document.createElement('div')
        container.id = generateAdElementId()
        if (fillSlot) {
            if (getComputedStyle(slot).position === 'static') {
                slot.style.position = 'relative'
            }
            container.style.position = 'absolute'
            container.style.inset = '0'
        }
        slot.appendChild(container)
        return container
    }

    private scheduleRefresh(publicBanner: PageAd, refreshInterval: number): void {
        if (!this.pageBanners.has(publicBanner)) {
            return
        }
        const timerId = window.setTimeout(() => {
            const isViewable = publicBanner.containerElId && isElementViewable(publicBanner.containerElId)
            if (isViewable) {
                publicBanner.refresh()
            }
            this.scheduleRefresh(publicBanner, refreshInterval)
        }, refreshInterval * 1000)
        this.refreshTimers.set(publicBanner, timerId)
    }

    setOutOfPageAdPlaybackState(play: boolean): void {
        for (const publicBanner of this.outOfPageBanners) {
            publicBanner.setPlaybackState(play)
        }
    }

    shutdown() {
        for (const publicBanner of this.pageBanners) {
            publicBanner.destroy()
        }
        for (const publicBanner of this.outOfPageBanners) {
            publicBanner.gracefullyDestroy('ad_free_mode_enabled')
        }
    }

    getTargeting(): AdTargeting {
        return structuredClone(this.currentTargeting)
    }

    updateTargeting(targeting: AdTargeting) {
        mergeTargeting(this.currentTargeting, targeting)

        this.headerBidding?.updateTargeting(this.currentTargeting)

        for (const key in this.providers) {
            this.getProvidersByType(key as ProviderKey)
                .filter(isActiveProvider)
                .forEach((provider) => {
                    this.setProviderTargeting(provider, this.currentTargeting)
                })
        }
    }

    private setProviderTargeting(provider: AdProvider, targeting: AdTargeting) {
        try {
            provider.updateTargeting(targeting)
        } catch {
            // provider threw an error, nothing we can do
        }
    }

    getAdMaxTimeout(placement?: AdType): number {
        return this.getProvidersByType(placement || '*')
            .filter((p): p is AdProvider | LoadingProvider => !('failed' in p))
            .reduce((accumulator, provider) => accumulator + provider.timeout, 0)
    }
}
